/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Button, Grid, Hr, Icon, IconButton, List, Spacer, Spinner, Table } from "@cd2/components"
import { Fields, Form2 } from "@cd2/forms"
import { fieldEvents, useField } from "@cd2/forms/build/es6/Form2"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { H2, H3, H4, P, TextWrap } from "@cd2/text"
import { useTheme } from "@cd2/theme/build/es6"
import { titleize } from "@cd2/tools"
import React, { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useHistory } from "react-router-dom"
import { dynamicMessageContents } from "../../Components/Buttons/EmailWith365Button"
import { ConfirmCancelDialogue } from "../../Components/ConfirmCancelDialogue"
import { colors, themeColors } from "../../Components/EOSThemeProvider"
import { HrRegionSelect } from "../../Components/FormFields/HrRegionSelect"
import { ImageUploadField } from "../../Components/FormFields/ImageUploadField"
import { MultiTextField } from "../../Components/FormFields/MultiTextField"
import { PaymentLocationsField } from "../../Components/FormFields/PaymentLocationsField"
import { PreviewFeature } from "../../Components/PreviewFeatures"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { ControlledPageTabs } from "../../Components/PageTabs"
import { BranchSelect } from "../../Components/SelectLists/BranchSelect"
import { ContactSelectList } from "../../Components/SelectLists/ContactSelectList"
import { MultiUserSelectList } from "../../Components/SelectLists/MultiUserSelectList"
import { SupplierSelect } from "../../Components/SelectLists/SupplierSelectList"
import { WarehouseSelect } from "../../Components/SelectLists/WarehouseSelectList"
import { MenuColumn } from "../../Components/Tables/utils"
import { autoCompleteOff } from "../../constants/autoComplete"
import { countries } from "../../constants/countries"
import { icons } from "../../constants/icons"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useConfirmation } from "../../hooks/useConfirmation"
import { useConfirmCancelConfirmation } from "../../hooks/useConfirmCancelConfirmation"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { NewForm2Submit } from "../../NewComponents/Buttons"
import { eRoutes } from "../../routes"
import { IRecord } from "../../types/schema"
import { spliceArray } from "../../utils/arrays"
import { defaultSettings, IBranchSettings } from "../../utils/useSettings"
import { MultiEmailField } from "./components/MultiEmailField"
import { HorizontalOverflow } from "../../Components/TableWrapper"
import {
  analysisCodeFields,
  getAnalysisCodeAttr,
  popFields,
  sopFields,
  cuFields,
} from "../../utils/utils"
import { TextArrayOptionsField } from "../../Components/FormFields/TextArrayOptionsField"
import { dublCorkBranchId, fortusEwBranchId } from "../../../shared/constants"
import { CustomerSelectList2 } from "../../Components/SelectLists/CustomerSelectList2"
import { MultiTextInput } from "../../Components/FormFields/MultiTextInput"
import { WriteOffCategoriesField } from "../../Components/FormFields/WriteOffCategoriesField"
import { FilePreview, FileUploader } from "@cd2/fileboy-react"

export type IBranchShowPageRecord = IRecord<
  "branches",
  | "name"
  | "active"
  | "settings"
  | "appearsOnStockLookup"
  | "currency_name"
  | "euro_pound_exchange_rate"
  | "is_fortus_ew_branch"
  | "branch_charges_pennies"
  | "branch_overhead_charges_pennies"
> & {
  consignmentWarehouse: IRecord<"warehouses", "name">
  settings: IBranchSettings
  users: IRecord<"skeem_users", "email">[]
  addresses: IRecord<"addresses", "label" | "name" | "line_1" | "postcode">[]
}

export function BranchesShowPage({
  branch,
  refetch,
}: {
  branch?: IBranchShowPageRecord
  refetch?: () => void
}) {
  const formRef = useRef<FormState | null>(null)
  const { currentUser } = useGlobalState()
  const history = useHistory()
  const skeem = window.skeem
  const alerts = useSiteAlerts()
  const { boxShadow } = useTheme()
  const [dpdEnabled, setDpdEnabled] = useState(
    branch?.settings.dpdService && branch.settings.dpdService !== "none",
  )

  async function handleSave(
    data: IBranchSettings & {
      adminSettings: {
        appearsOnStockLookup: boolean
        is_fortus_ew_branch: boolean
        branch_charges_pennies: number
        branch_overhead_charges_pennies: number
      }

      currency_name: string
      euro_pound_exchange_rate: number
      consignmentWarehouse: string

    },
  ) {
    const {
      adminSettings,
      currency_name,
      euro_pound_exchange_rate,
      consignmentWarehouse,
      ...settingsJson
    } = data
    settingsJson.deliveryOptions = settingsJson.deliveryOptions.filter(
      option => !!option.value.trim(),
    )

    console.log(consignmentWarehouse, "consignmentWarehouse")
    await skeem.mutate({
      branches: {
        update: {
          id: branch!.id,
          active: true,
          settings: settingsJson,
          ...adminSettings,
          currency_name,
          euro_pound_exchange_rate,
          consignmentWarehouse: consignmentWarehouse ? { set: consignmentWarehouse } : undefined,
        },
      },
    })
    alerts.genericSuccess("Saved")
    refetch?.()
    history.push(eRoutes["/manage-branch/edit"])
  }

  function loadWarehouses(query: string) {
    return skeem.fetch2<IRecord<"warehouses", "name">[]>({
      model: {
        name: "warehouses",
        attributes: ["name"],
        filter: {
          and: [
            { matches: { branch: branch?.id } },
            query && { matches: { name: { like: query } } },
          ].filter(Boolean),
        },
      },
    })
  }

  const formTabs: {
    header: string
    key: string
    Content: () => React.ReactNode
    icon: any
    hide?: boolean
  }[] = [
    {
      header: "Quotes & Orders",
      key: "quotes_orders",
      icon: "",
      Content: () => (
        <Grid gap={16}>
          {console.log("DID RENDER", "quotes")}
          <SettingsPanel header="Payment locations">
            <PaymentLocationsField field="paymentLocations" />
          </SettingsPanel>

          <SettingsPanel header="Quotes/Orders">
            <Form2.Money
              field="quoteCreateProjectThreshold"
              label="Amount to trigger project creation"
            />

            <Form2.Checkbox field="noValidDateOnQuotes" />

            <DefaultStockLocationField branch={branch} />

            <Form2.Checkbox
              field="showDiscountOption"
              label="Allow items to have a discount applied"
            />
            <Form2.Checkbox field="invoicesOnlyShowDiscountTotal" />

            <Form2.Checkbox
              field="showOriginalPriceWhenShowingDiscountOption"
              label="When showing discount option, original price and discount price will both be shown."
            />

            <Form2.Checkbox field="showBillingAddressOnOrders" />
            <Form2.Checkbox field="alwaysShowBillingForCashSalesOnOrders" />

            <div>
              <Form2.Number field="minimumLineItemMargin" prefix="%" />
              <P size="small">
                The minimum margin a user can enter for a line item on quotes and orders
              </P>
              <P size="small">Leave this blank to have no minimum.</P>
              <P size="small">Users can individually be allowed to bypass this minimum.</P>
            </div>

            <Form2.Checkbox
              field="hideProductInfoStandardPricing"
              label="Hide standard pricing from product info panel"
            />
            <Form2.Checkbox
              field="displayKits"
              label="Display Kit interface in quotes and orders"
            />

            <Form2.Checkbox field="useOrderPaymentMethod" />

            <div>
              <Form2.Checkbox
                field="autoDespatchOrdersInSage"
                label="Automatically despatch orders in Sage"
              />
              <P size="small">
                When enabled, exporting an order to Sage will also confirm the despatch of all
                outstanding lines on that order in Sage. Orders that were exported but could not be
                despatched can be despatched manually from the Sage export panel on the order.
              </P>
            </div>
          </SettingsPanel>

          <SettingsPanel header="PDF Generation">
            <Form2.TextArea
              field="pdfAddress"
              label="Address"
              rows={10}
              autoComplete={autoCompleteOff}
            />

            <Form2.Text field="vatNumber" label="VAT Number" />
            <Form2.Text field="companyRegNumber" label="Company Registration Number" />

            <Form2.Text field="pdfIBAN" label="IBAN" />
            <Form2.Text field="pdfSWIFT" label="SWIFT" />
            <Form2.Text field="pdfIBANCashSales" label="IBAN for Cash Sales" />
            <Form2.Text field="pdfSWIFTCashSales" label="SWIFT for Cash Sales" />

            <Form2.Text
              field="pdfDiscrepancyTime"
              label="Time customers can report discrepancies (defaults to 2 days)"
            />

            <Form2.Text
              field="pdfReturnsTime"
              label="Time customers can return unwanted goods (defaults to 60 days)"
            />
          </SettingsPanel>

          <SettingsPanel header="Other Settings">
            <Form2.Text field="costCentre" label="Cost Centre" />
            <Form2.Checkbox field="displayQuotesWithSellingPriceAndDiscounts" />
            <Form2.Checkbox field="alwaysPresentChooseDeliveryAddress" />
            <Form2.WithField
              field="useExplicitAllocations"
              render={field => (
                <React.Fragment>
                  <Form2.Checkbox
                    field="displayOrderQtyOnPickingTicketWhenUsingExplicitAlloc"
                    label="Display order quantity on picking ticket when using explicit allocations"
                    disabled={!field.getValue()}
                  />
                </React.Fragment>
              )}
            />
            <Form2.Checkbox field="displayUnallocatedWarningBanner" />
            <Form2.Checkbox field="displayAverageMargins" />
            <Form2.Checkbox
              field="useQuoteOrderStandardPricing"
              label="Use standard price with discounts for quote & order pricing"
            />
            <Form2.Checkbox
              field="showPriceOnPickingTicket"
              label="Show price column on picking ticket"
            />
            <Form2.Checkbox
              field="canUseOpportunityCertificate"
              label="Allow this branch to use the opportunity certificate"
            />
            <Form2.Checkbox
              field="showPriceOnPickingTicket"
              label="Show price column on picking ticket"
            />
            <Form2.Checkbox field="allowDisableAutoOrderRequestEmail" />
            {/* inverting the state so that in the UI it's a bit more useable. */}
            {/* leaving as a "is disabled" check so that default values all start true by default */}
            <Form2.WithField<boolean>
              field="defaultAutoOrderRequestEmailDisabled"
              render={field => (
                <Form2.WithField<boolean>
                  field="allowDisableAutoOrderRequestEmail"
                  render={settingField => (
                    <Fields.Checkbox
                      label="Default Automatic order acknowledgement email state"
                      onChange={b => field.setValue(!b)}
                      value={!field.getValue()}
                      disabled={!settingField.getValue()}
                    />
                  )}
                />
              )}
            />
            <div>
              <Form2.Checkbox field="alertPctDiff" label="Price difference alert" />
              <P size="small">
                Show pop up alert when a the price difference between the average buying price and
                preferred list price is greater than 7%.
              </P>
            </div>
            <div>
              <Form2.Checkbox field="canSetOrderWarehouse" />
              <P size="small">
                Allow setting of warehouse on orders. If branch is barscan, will disable barscan
                actions for this order and ensure all locations belong to the specified warehouse.
              </P>
            </div>
            <div>
              <Form2.Checkbox
                field="orderRequiredDateMandatory"
                label="Required (delivery) date is mandatory on orders"
              />
              <P size="small">When enabled, orders saved via the Orders form cannot be saved without a required date.</P>
            </div>
            <div>
              <Form2.Checkbox
                field="useCashSalesOrderAcknowledgementTitleSwap"
                label="Show Invoice title for cash sales order acknowledgement"
              />
              <P size="small">
                When enabled, order acknowledgement PDF for cash sales will always show as
                {'"Invoice"'}.
              </P>
            </div>
            <div>
              <Form2.Checkbox
                field="useAddressLengthValidation"
                label="Validate address length when using a Fedex or DPD delivery service"
              />
              <P size="small">
                When enabled, if the delivery method is set to Fedex, the address length will be
                validated to 20chars or less.
              </P>
              <P size="small">
                If the delivery service is set to DPD, the address length is validated to 35chars or
                less.
              </P>
            </div>
            <Form2.Checkbox field="disableBulkExporting" label="Disable bulk exporting orders" />
          </SettingsPanel>
        </Grid>
      ),
    },
    {
      header: "Currency and Exchange",
      key: "currency",
      icon: "",
      Content: () => (
        <Grid gap={16}>
          {console.log("DID RENDER", "currency")}
          <SettingsPanel header="Currency and Exchange">
            <Form2.Select
              field="currency_name"
              label="Currency"
              options={[{ label: "Pound Sterling" }, { label: "Euro" }]}
              valueExtractor={(opt: { label: string }) => opt?.label ?? ""}
              getOptionLabel={opt => opt.label}
              getOptionValue={opt => opt.label}
            />

            <Form2.Select
              field="sageDefaultCurrency"
              label="sageDefaultCurrency"
              options={[{ label: "GBP" }, { label: "EUR" }]}
              valueExtractor={(opt: { label: string }) => opt?.label ?? ""}
              getOptionLabel={opt => opt.label}
              getOptionValue={opt => opt.label}
            />

            <Form2.Select
              field="EOSDefaultCurrency"
              label="EOSDefaultCurrency"
              options={[{ label: "GBP" }, { label: "EUR" }]}
              valueExtractor={(opt: { label: string }) => opt?.label ?? ""}
              getOptionLabel={opt => opt.label}
              getOptionValue={opt => opt.label}
            />

            <Form2.Number field="euro_pound_exchange_rate" />

            <Form2.Select
              options={countries.map(country => ({ value: country.name, label: country.name }))}
              label="defaultCountry"
              field="defaultCountry"
              valueExtractor={(val: any) => val.value}
            />
          </SettingsPanel>

          <SettingsPanel header="Statement PDFs">
            <Form2.Text
              field="statementBankAccountNumber"
              label="Account Number for regular customers"
            />
            <Form2.Text field="statementBankSortCode" label="Sort Code for regular customers" />
            <div />
            <Form2.Text
              field="statementBankAccountNumberCashSale"
              label="Account Number for cash sales customers"
            />
            <Form2.Text
              field="statementBankSortCodeCashSale"
              label="Sort Code for cash sales customers"
            />
          </SettingsPanel>

          <SettingsPanel header="Other Settings">
            <Form2.WithField<{
              branch_charges_pennies: number
              branch_overhead_charges_pennies: number
            }>
              field="adminSettings"
              render={field => (
                <React.Fragment>
                  <Fields.Price
                    label="Branch Charges"
                    onChange={value =>
                      field.setValue({ ...field.getValue(), branch_charges_pennies: value as any })
                    }
                    value={field.getValue()?.branch_charges_pennies ?? null}
                    inPennies
                  />
                  <Fields.Price
                    label="Branch Overheads"
                    onChange={value =>
                      field.setValue({
                        ...field.getValue(),
                        branch_overhead_charges_pennies: value as any,
                      })
                    }
                    value={field.getValue()?.branch_overhead_charges_pennies ?? null}
                    inPennies
                  />
                </React.Fragment>
              )}
              subscribe
            />
            <Form2.Checkbox
              field="allowChangingQuoteOrderCurrency"
              label="Allow changing the target currency for quotes and orders"
            />
          </SettingsPanel>
        </Grid>
      ),
    },
    {
      header: "Warehouses",
      key: "warehouses",
      icon: "",
      Content: () => (
        <Grid gap={16}>
          <SettingsPanel
            header="Warehouses"
            subtitle="Select warehouses that are eligible for selecting when building a quote / order"
          >
            <Form2.AsyncSelect
              field="warehouses"
              label="Warehouses"
              loadOptions={loadWarehouses}
              getOptionLabel={opt => opt.name}
              getOptionValue={opt => opt.id}
              isMulti
            />

            <Form2.AsyncSelect
              field="defaultIntercoWarehouse"
              label="Default Interco Warehouse"
              loadOptions={loadWarehouses}
              getOptionLabel={opt => opt.name}
              getOptionValue={opt => opt.id}
            />

            <Form2.AsyncSelect
              field="defaultDeliveryProductLocation"
              label="Default Delivery Warehouse"
              loadOptions={loadWarehouses}
              getOptionLabel={opt => opt.name}
              getOptionValue={opt => opt.id}
            />

            <Form2.AsyncSelect
              field="goodsInTransitWarehouse"
              label="Goods In Transit Warehouse"
              loadOptions={loadWarehouses}
              getOptionLabel={opt => opt.name}
              getOptionValue={opt => opt.id}
            />

            <Form2.AsyncSelect
              field="returnsInTransitWarehouse"
              label="Returns In Transit Warehouse"
              loadOptions={loadWarehouses}
              getOptionLabel={opt => opt.name}
              getOptionValue={opt => opt.id}
            />

            <Form2.AsyncSelect
              field="directDeliveryWarehouse"
              label="Direct Delivery Warehouse"
              loadOptions={loadWarehouses}
              getOptionLabel={opt => opt.name}
              getOptionValue={opt => opt.id}
              isClearable
            />

            <Form2.AsyncSelect
              field="returnsWarehouse"
              label="Returns Warehouse"
              loadOptions={loadWarehouses}
              getOptionLabel={opt => opt.name}
              getOptionValue={opt => opt.id}
              isClearable
            />
          </SettingsPanel>

          <SettingsPanel
            header="Stock Warehouses"
            subtitle="Select which warehouses contribute to stock totals."
          >
            <WarehouseWatcher />
            <Form2.WithField<IBranchSettings["warehouses"]>
              field="warehouses"
              render={field => {
                if (field.getValue().length === 0) {
                  return (
                    <Form2.AsyncSelect
                      field="stockWarehouses"
                      label="Stock Warehouses"
                      loadOptions={loadWarehouses}
                      getOptionLabel={opt => opt.name}
                      getOptionValue={opt => opt.id}
                      isMulti
                    />
                  )
                }
                return (
                  <Form2.Select
                    field="stockWarehouses"
                    label="Stock Warehouses"
                    options={field.getValue()}
                    getOptionLabel={opt => opt.name}
                    getOptionValue={opt => opt.id}
                    isMulti
                  />
                )
              }}
            />
            <Form2.WithField<string>
              field="consignmentWarehouse"
              render={field => (
                <WarehouseSelect
                  value={field.getValue()}
                  onChange={field.setValue}
                  label="Consignment warehouse"
                />
              )}
              subscribe
            />
          </SettingsPanel>
        </Grid>
      ),
    },
    {
      header: "Delivery Settings",
      key: "delivery",
      icon: "",
      Content: () => (
        <Grid gap={16}>
          <SettingsPanel header="Addresses">
            <BranchAddressSelect
              fieldName="branchPoAddress"
              label="Branch PO address"
              isClearable
            />
            <div style={{ padding: 10, border: "lightgrey 1px solid" }}>
              <MultiTextField field="deliveryOptions" />
            </div>
          </SettingsPanel>

          <SettingsPanel header="Delivery Settings" subtitle="All strings are limited to 30 chars">
            <Form2.Checkbox
              field="autoEmailCashSalesInvoice"
              label="Automatically send invoice email to cash sales customer after delivery/order export"
            />
            <Form2.Select
              field="dpdService"
              label="Service"
              options={[
                { label: "None (Disabled)", value: "none" },
                { label: "DPD", value: "dpd" },
                { label: "DPD Local", value: "dpd-local" },
                { label: "DPD Ireland", value: "dpd-ireland" },
              ]}
              valueExtractor={(opt: { value: string }) => opt.value}
            />
            <Form2.WithField
              field="dpdService"
              subscribe={true}
              render={field =>
                field.getValue() === "dpd-ireland" ? (
                  <React.Fragment>
                    <Form2.Text
                      field="dpdIrelandUrl"
                      label="DPD Ireland URL"
                      inputProps={{ list: "url_suggestions" }}
                    />
                    <datalist id="url_suggestions">
                      <option value="https://pre-prod-papi.dpd.ie/common/api">
                        https://pre-prod-papi.dpd.ie/common/api
                      </option>
                      <option value="https://papi.dpd.ie/common/api">
                        https://papi.dpd.ie/common/api
                      </option>
                    </datalist>
                  </React.Fragment>
                ) : null
              }
            />

            <Form2.Text
              field="dpdUsername"
              label="Username"
              disabled={!dpdEnabled}
              validations={{
                presence: dpdEnabled && process.env.NODE_ENV === "production",
              }}
            />
            <Form2.Text
              field="dpdProductPrefixes"
              label="DPD Product Prefixes"
              disabled={!dpdEnabled}
              postFieldText={
                <P size="small">
                  Multiple prefixes can be added by separating them with a comma (,)
                </P>
              }
            />
            <Form2.Text
              field="dpdPassword"
              label="Password"
              disabled={!dpdEnabled}
              validations={{
                presence: dpdEnabled && process.env.NODE_ENV === "production",
              }}
            />
            <Form2.Text
              field="dpdAccountNumber"
              label="DPD Account Number"
              disabled={!dpdEnabled}
              validations={{
                presence: dpdEnabled && process.env.NODE_ENV === "production",
              }}
            />
            <Form2.Text field="fedexAccountNumber" label="FedEx Account Number" />
            <Form2.Text
              field="dpdCountryCode"
              label="Country Code"
              disabled={!dpdEnabled}
              validations={{ presence: dpdEnabled }}
            />
            <Form2.Text field="dpdPostcode" label="Postcode" disabled={!dpdEnabled} />
            <Form2.Text field="dpdOrganisation" label="Organisation" disabled={!dpdEnabled} />
            <Form2.Text
              field="dpdStreet"
              label="Street"
              disabled={!dpdEnabled}
              validations={{ presence: dpdEnabled }}
            />
            <Form2.Text field="dpdLocality" label="Locality" disabled={!dpdEnabled} />
            <Form2.Text field="dpdTown" label="Town" disabled={!dpdEnabled} />
            <Form2.Text field="dpdCounty" label="County" disabled={!dpdEnabled} />
            <Form2.Text field="dpdContactName" label="Contact Name" disabled={!dpdEnabled} />
            <Form2.Text field="dpdTelephone" label="Telephone" disabled={!dpdEnabled} />
            <div style={{ padding: 10, border: "lightgrey 1px solid" }}>
              <h4>DPD Notification Emails</h4>
              <p>Recipients of the delivery confirmation email (in addition to the customer).</p>

              <EmailsFields field="dpdNotificationEmails" />
            </div>
            <Form2.Select
              field="dpdLabelFormat"
              label="Printer Format"
              options={[
                { label: "None (Disabled)", value: "none" },
                { label: "CLP: Citizen Thermal Printer", value: "citizen" },
                { label: "EPL: Eltron Printer", value: "eltron" },
                { label: "HTML: Standard A4 Printer", value: "html" },
                { label: "Zebra (Ireland only)", value: "zebra" },
              ]}
              valueExtractor={(opt: { value: string }) => opt.value}
            />
            <Form2.Text field="dpdPrinterPath" />
            <Form2.Text field="fedexPrinterPath" />
            <Form2.Text field="a4PrinterPath" />
            <Form2.TextArea
              field="orderAcknowledgementFooterText"
              label="Custom Order Acknowledgement footer text"
            />
            <Form2.TextArea field="quoteFooterText" label="Custom Quote footer text" />
          </SettingsPanel>
        </Grid>
      ),
    },
    {
      header: "Branch Settings",
      key: "branch",
      icon: "",
      Content: () => (
        <Grid gap={16}>
          <SettingsPanel
            header="Branch Directors"
            subtitle={
              <P>
                Branch directors will receive specific notifications that are relevant for the
                branch, such as large quote alerts.
              </P>
            }
          >
            <Form2.WithField<{ id: string; name: string; skeem_email: string }[]>
              field="branchDirectorUsers"
              render={field => (
                <MultiUserSelectList
                  onChange={field.setValue}
                  value={field.getValue()}
                  accessibleBranchId={branch?.id}
                  filter={{ matches: { userAccountType: "branch" } }}
                />
              )}
            />
          </SettingsPanel>
          <SettingsPanel header="Barscan Integration">
            <Form2.WithForm
              subscribe={{ dataChange: true, submitting: true }}
              render={form => {
                const field = form.getField("barScanOrders")
                const defaultPartShip = form.getField("defaultPartShip")
                return (
                  <Fields.Checkbox
                    onChange={state => {
                      field.setValue(state)
                      if (!state) {
                        defaultPartShip.setValue(false)
                      }
                    }}
                    value={field.getValue()}
                    label="Integrate Barscan for orders"
                  />
                )
              }}
            />

            <Form2.WithField<boolean>
              field="barScanOrders"
              render={field => (
                <React.Fragment>
                  <Form2.Checkbox
                    field="defaultPartShip"
                    label="Set the default value for part ship"
                    disabled={!field.getValue()}
                  />
                  {!field.getValue() && (
                    <P color="grey" size="small">
                      Enable Barscan for orders to use this integration.
                    </P>
                  )}
                </React.Fragment>
              )}
            />
            <Form2.Text field="barscanUrl" label="Barscan Integration Address" />
            <PreviewFeature feature="barscanWarehouseTransferV2">
              <Form2.WithField<boolean>
                field="barScanOrders"
                render={field => (
                  <Form2.Checkbox
                    field="barscanWarehouseTransferV2"
                    label="Send created date, requested date and SHUNT flag with internal transfers (WarehouseTransferRequestV2)"
                    disabled={!field.getValue()}
                  />
                )}
              />
            </PreviewFeature>
          </SettingsPanel>
          <SettingsPanel header="Tasks">
            <Form2.Checkbox field="viewAllBranchTasksInTheAllTasksView" />
          </SettingsPanel>
          <SettingsPanel header="Other Settings">
            <div
              css={css`
                position: relative;
                z-index: 20;
              `}
            >
              <Form2.Select
                field="company"
                label="Company"
                options={[
                  { label: "Fortus", value: "fortus" },
                  { label: "SSP", value: "ssp" },
                  { label: "EGS", value: "egs" },
                ]}
                valueExtractor={(opt: { value: string }) => opt.value}
              />
            </div>

            <Form2.Checkbox
              field="canViewPoShortfallItemsPanel"
              postFieldText={
                <P size="small">
                  When enabled, users creating purchase orders will be able to view supplier
                  products that currently have a shortfall.
                </P>
              }
            />
            <Form2.WithField<string>
              field="productImageFallbackFileboyId"
              render={field => (
                <ImageUploadField
                  label="Product Image Fallback"
                  onChange={value => field.setValue(value?.id ?? "")}
                  value={field.getValue()}
                  isClearable
                />
              )}
            />
            {/* 665945cb-4fe6-4af4-9fa0-8f51f7959ed1 */}
            {currentUser.isSuperAdmin && <Form2.Text field="productImageFallbackFileboyId" />}
            <Form2.Checkbox field="automaticallyGenerateStockTakes" />
            <Form2.Checkbox
              field="canBulkAssignWarehouse"
              label="Allow bulk changing of Purchase order and Sales Order items warehouse"
            />
            <Form2.Text field="eoriNo" label="Branch EORI NO. appears on commodity invoices" />
            <HrRegionSelect />
          </SettingsPanel>
          <SettingsPanel header="Stock Takes">
            {branch && <MultiProductSelect field="alwaysStockCheckProducts" branchId={branch.id} />}
            <Form2.Checkbox
              label="Use MRGE API for stock takes exporting"
              field="stockTakesUseMrge"
            />
            <Form2.Text
              label="MRGE Base URL"
              field="mrgeBaseUrl"
              placeholder="e.g. http://10.0.0.1:91"
            />
            <div>
              <Form2.Text field="writeOffCategory" label="MRGE Write-Off Category" />
              <P size="small">
                The write-off category name sent to MRGE when exporting a stock take write-off.
              </P>
              <P size="small">
                This is used as the default when a line in the stock take export does not specify
                its own category. If left blank, "Unspecified" will be used.
              </P>
            </div>
            <div>
              <H4>Write-Off Categories</H4>
              <P size="small">
                The categories that can be picked for each item when a stock take write-off is
                exported. The nominal code is the one the category posts against in Sage, and is
                what the Stock Take Report shows for those items.
              </P>
              <Spacer size={8} />
              <WriteOffCategoriesField field="writeOffCategories" />
            </div>
          </SettingsPanel>
          <SettingsPanel header="Rich Quote Data">
            <Form2.Phone field="richQuoteSalesPhone" label="Sales Phone" />
            <Form2.Phone field="richQuoteTechnicalPhone" label="Technical Phone" />
            <Form2.Email field="richQuoteEmail" label="Email" />
            <Form2.Text field="richQuoteWebsite" label="Website" />
            <Form2.TextArea field="richQuoteAddress" label="Address" rows="auto" />
            <Form2.WithField<string>
              field="branchImageId"
              render={field => (
                <ImageUploadField
                  onChange={file => field.setValue(file?.id)}
                  value={field.getValue()}
                  label="Branch Image"
                  isClearable
                />
              )}
            />
          </SettingsPanel>
          <SettingsPanel header="RMA settings">
            <MultiTextInput
              field="rmaNotePresets"
              defaultOptions={[
                "Please note no final crediting decision is made until the item(s) are inspected by RMA team.",
                "Please make sure all included RMA NUMBERS TO BE WRITTEN ON THE OUTER BOX, this makes sure receiving warehouse can book all RMA in.",
              ]}
            />
          </SettingsPanel>
        </Grid>
      ),
    },
    {
      header: "Interbranch settings",
      key: "interbranch",
      icon: "",
      Content: () => (
        <Grid gap={16}>
          <SettingsPanel header="Other Settings">
            <Form2.Checkbox
              field="canRequestDirectDeliveryOR"
              label="Allow branch to request products be sent to the direct delivery warehouse"
            />
            <Form2.Checkbox field="excludeFromGroupedPricing" />
            <Form2.Checkbox field="contributeToAverageMargins" />
            <Form2.Checkbox field="allowInternalOrderTransfers" />
            <Form2.WithField<{ appearsOnStockLookup: boolean }>
              field="adminSettings"
              render={field => (
                <Form2.Form onChange={field.setValue} data={field.getValue()}>
                  <Form2.Checkbox field="is_fortus_ew_branch" />
                </Form2.Form>
              )}
              subscribe
            />
          </SettingsPanel>
        </Grid>
      ),
    },
    {
      header: "Internal branch settings",
      key: "internal-settings",
      icon: "",
      Content: () => (
        <Grid gap={16}>
          <ManageOrderRequestBranches branchId={branch?.id} />
        </Grid>
      ),
    },
    {
      header: "Notifications & Emails",
      key: "notifications",
      icon: "",
      Content: () => (
        <Grid gap={16}>
          <SettingsPanel
            header="Notification Emails"
            subtitle="Manage who is emailed when a notification event occurs"
          >
            <div>
              <h4>Customer created order request</h4>
              <Spacer size={4} />
              <MultiEmailField field="notificationEmailsCustomerJobCreated" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>Customer commented on order request</h4>
              <Spacer size={4} />
              <MultiEmailField field="notificationEmailsCustomerJobComment" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>Customer accepted an quote</h4>
              <Spacer size={4} />
              <MultiEmailField
                field="notificationEmailsCustomerQuoteAccepted"
                branchId={branch?.id}
              />
            </div>
            <Hr />
            <div>
              <h4>Order request assigned to an order</h4>
              <Spacer size={4} />
              <MultiEmailField
                field="notificationEmailsOrderRequestAssignedToOrder"
                branchId={branch?.id}
              />
            </div>
            <Hr />
            <div>
              <h4>Order request created</h4>
              <Spacer size={4} />
              <MultiEmailField
                field="notificationEmailsOrderRequestCreated"
                branchId={branch?.id}
              />
            </div>
            <Hr />
            <div>
              <h4>Daily Branch Stock Report</h4>
              <Spacer size={4} />
              <MultiEmailField field="dailyBranchStockReport" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>Holiday Request Notifications</h4>
              <Spacer size={4} />
              <MultiEmailField field="notificationEmailsHolidayRequests" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>Holiday Request Report (Weekly)</h4>
              <Spacer size={4} />
              <MultiEmailField field="holidayRequestReportEmails" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>Product Group Margin Alerts</h4>
              <Spacer size={4} />
              <MultiEmailField
                field="notificationEmailsProductGroupMargins"
                branchId={branch?.id}
              />
            </div>
            <Hr />
            <div>
              <h4>New Customer Account Alerts</h4>
              <Spacer size={4} />
              <MultiEmailField field="notificationEmailsNewCustomerAccount" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>New Trade Application Alerts</h4>
              <Spacer size={4} />
              <MultiEmailField
                field="notificationEmailsNewTradeApplication"
                branchId={branch?.id}
              />
            </div>
            <Hr />
            <div>
              <h4>Trade Application Actioned Alerts</h4>
              <Spacer size={4} />
              <MultiEmailField
                field="notificationEmailsActionedTradeApplications"
                branchId={branch?.id}
              />
            </div>
            <Hr />
            <div>
              <h4>New RMA Alerts</h4>
              <Spacer size={4} />
              <MultiEmailField field="notificationEmailsNewReturnRequest" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>Daily Branch Stock Report</h4>
              <Spacer size={4} />
              <MultiEmailField field="dailyBranchStockReport" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>Automatic Stock Count Generation</h4>
              <Spacer size={4} />
              <MultiEmailField field="notificationEmailsAlertStockTake" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>Upcoming Probation Review</h4>
              <Spacer size={4} />
              <MultiEmailField field="notificationEmailsProbationReview" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>Stock Take Exported</h4>
              <Spacer size={4} />
              <MultiEmailField field="notificationEmailsStockTakeExported" branchId={branch?.id} />
            </div>
            <Hr />
            <div>
              <h4>Daily cash sales report</h4>
              <P blockSpacing={0}>
                Emailed at 6pm with the day&apos;s cash sales for this branch. Not sent on days with
                no cash sales.
              </P>
              <Spacer size={4} />
              <MultiEmailField
                field="notificationEmailsDailyCashSalesReport"
                branchId={branch?.id}
              />
            </div>
            {/* Only for HUB */}
            {branch && [fortusEwBranchId, dublCorkBranchId].includes(branch.id) && (
              <React.Fragment>
                <Hr />
                <div>
                  <h4>Daily branch sales report</h4>
                  <Spacer size={4} />
                  <MultiEmailField
                    field="notificationEmailsDailyBranchSalesReport"
                    branchId={branch?.id}
                  />
                </div>
              </React.Fragment>
            )}
            {branch && [fortusEwBranchId].includes(branch.id) && (
              <React.Fragment>
                <Hr />
                <div>
                  <h4>Daily Order requests report</h4>
                  <Spacer size={4} />
                  <MultiEmailField
                    field="notificationEmailsDailyOrderRequestsReport"
                    branchId={branch?.id}
                  />
                </div>
              </React.Fragment>
            )}
          </SettingsPanel>
          <SettingsPanel
            gridGap={32}
            header="Email Content"
            subtitle={
              <div>
                <P blockSpacing={0}>Add a custom message to outlook emails generated.</P>
                <P blockSpacing={0}>
                  You can add in dynamic data by entering {"{{variable_name}}"} into the editor.
                  <br />
                  It is possible for variables to not have a value, in which case they will appear
                  as their variable name.
                </P>
                <P blockSpacing={0}>
                  View the preview under the editor to see what it will look like.
                </P>
                <P size="small">
                  example message:{" "}
                  <div>
                    {"{{first_name}}"} {"{{last_name}}"},<br />
                    Your quote {"{{identifier}}"} for reference: {"{{reference}}"} has been
                    accepted!
                  </div>
                </P>
              </div>
            }
          >
            <div
              css={css`
                padding: 16px;
                box-shadow: ${boxShadow.sm};
              `}
            >
              <Form2.RichText
                field="quoteEmailContent"
                preFieldText={<P size="small">leave blank to use default</P>}
              />
              <div
                css={css`
                  padding: 8px;
                  border: 1px solid grey;
                  width: max-content;
                  margin: 4px 0;
                `}
              >
                <P>Variables</P>
                <P size="small">
                  Record: {"{{reference}}"} {"{{identifier}}"}
                </P>
                <P size="small">
                  Contact: {"{{first_name}}"} {"{{last_name}}"}
                </P>
              </div>
              <Form2.WithField<string>
                field="quoteEmailContent"
                render={field => (
                  <div>
                    <div
                      css={css`
                        padding: 8px;
                        border: 1px dashed ${themeColors.grey};
                      `}
                    >
                      <h4>Preview</h4>
                      {!field.getValue() && <P size="small">Enter a message to see the preview</P>}
                      <TextWrap rawHtml>
                        {dynamicMessageContents(field.getValue(), {
                          first_name: "John",
                          last_name: "Smith",
                          identifier: "PO112233",
                          reference: "C_REF_123",
                        })}
                      </TextWrap>
                    </div>
                    <Spacer size={4} />
                    <Hr />
                  </div>
                )}
              />
            </div>

            <div
              css={css`
                padding: 16px;
                box-shadow: ${boxShadow.sm};
              `}
            >
              <Form2.RichText
                field="orderAcknowledgementEmailContent"
                preFieldText={<P size="small">leave blank to use default</P>}
              />
              <div
                css={css`
                  padding: 8px;
                  border: 1px solid grey;
                  width: max-content;
                  margin: 4px 0;
                `}
              >
                <P>Variables</P>
                <P size="small">
                  Record: {"{{reference}}"} {"{{identifier}}"}
                </P>
                <P size="small">
                  Contact: {"{{first_name}}"} {"{{last_name}}"}
                </P>
              </div>
              <Form2.WithField<string>
                field="orderAcknowledgementEmailContent"
                render={field => (
                  <div>
                    <div
                      css={css`
                        padding: 8px;
                        border: 1px dashed ${themeColors.grey};
                      `}
                    >
                      <h4>Preview</h4>
                      {!field.getValue() && <P size="small">Enter a message to see the preview</P>}
                      <TextWrap rawHtml>
                        {dynamicMessageContents(field.getValue(), {
                          first_name: "John",
                          last_name: "Smith",
                          identifier: "PO112233",
                          reference: "C_REF_123",
                        })}
                      </TextWrap>
                    </div>
                    <Spacer size={4} />
                    <Hr />
                  </div>
                )}
              />
            </div>

            <div
              css={css`
                padding: 16px;
                box-shadow: ${boxShadow.sm};
              `}
            >
              <Form2.RichText
                field="purchaseOrderEmailContent"
                preFieldText={<P size="small">leave blank to use default</P>}
              />
              <div
                css={css`
                  padding: 8px;
                  border: 1px solid grey;
                  width: max-content;
                  margin: 4px 0;
                `}
              >
                <P>Variables</P>
                <P size="small">Supplier: {"{{supplierName}}"}</P>
                <P size="small">Purchase Order: {"{{orderNumber}}"}</P>
              </div>
              <Form2.WithField<string>
                field="purchaseOrderEmailContent"
                render={field => (
                  <div>
                    <div
                      css={css`
                        padding: 8px;
                        border: 1px dashed ${themeColors.grey};
                      `}
                    >
                      <h4>Preview</h4>
                      {!field.getValue() && <P size="small">Enter a message to see the preview</P>}
                      <TextWrap rawHtml>
                        {dynamicMessageContents(field.getValue(), {
                          supplierName: "Test Supplier",
                          orderNumber: "OR_123123",
                        })}
                      </TextWrap>
                    </div>
                    <Spacer size={4} />
                    <Hr />
                  </div>
                )}
              />
            </div>
          </SettingsPanel>

          <SettingsPanel header="Price List Emails">
            <Form2.Checkbox field="sendPriceListReminders" label="Send price list reminders" />
            <Form2.Checkbox field="sendMonthlyPriceLists" />
          </SettingsPanel>
        </Grid>
      ),
    },
    {
      header: "Customer App & Settings",
      key: "customer",
      icon: "",
      Content: () => (
        <Grid gap={16}>
          <SettingsPanel header="Branch details (shown to your customers)">
            <Form2.Text field="branchName" label="Displayed name" />
            <Form2.Email field="branchEmail" label="Email" />
            <Form2.Phone field="branchPhone" label="Phone" />
            <Form2.Text
              field="branchWebsite"
              label="Website"
              placeholder="https://fortuslive.com"
            />
            <Form2.TextArea field="branchAddress" label="Address" rows={10} />
            <Form2.Text field="branchPostcode" label="Postcode (used for map)" />
            <div style={{ padding: 10, border: "lightgrey 1px solid" }}>
              <h4>Contact/Support Emails</h4>
              <EmailsFields field="branchEmails" />
            </div>
          </SettingsPanel>
          {branch?.id == fortusEwBranchId && (
            <SettingsPanel
              header="Branch Document"
              subtitle="A downloadable document that will be displayed on the customer app branches page under the additional emails"
            >
              <Form2.WithField<IFileboyFile | undefined>
                field="branch_pdf"
                render={field => (
                  <FileField onChange={value => field.setValue(value)} file={field.getValue()} />
                )}
              />
            </SettingsPanel>
          )}
          <CustomerSettingsForm />
        </Grid>
      ),
    },
    {
      header: "Super Admin",
      key: "super_admin",
      icon: "",
      hide: !currentUser.canEditGlobalUserPermissions,
      Content: () => (
        <Grid gap={16}>
          <SettingsPanel header="Super Admin Settings">
            <Form2.Checkbox field="useExplicitAllocations" />
            <Form2.Checkbox
              field="allowZeroPriceQuotesOrders"
              label="Allow quotes and orders to have a 0 (zero) price"
            />
            <Form2.Text
              field="exportEosIdentifierPrefix"
              label="Exported to Sage Fortus Live Identifier Prefix"
            />
            <EosIDPrefixValidation />
            <Form2.Text
              field="sageCarriageItemProductCode"
              label="Purchase Order Export Carriage Item Product Code"
              placeholder="Carriage"
              postFieldText={
                <P size="small">
                  Leaving this field blank will set the product code to {'"Carriage"'}
                </P>
              }
            />
            <Form2.WithField<{ appearsOnStockLookup: boolean }>
              field="adminSettings"
              render={field => (
                <Form2.Form onChange={field.setValue} data={field.getValue()}>
                  <Form2.Checkbox
                    field="appearsOnStockLookup"
                    postFieldText={
                      <P size="small">
                        Should this branches stock be available to view when looking at the
                        quote/order edit page sage item
                      </P>
                    }
                  />
                </Form2.Form>
              )}
              subscribe
            />
            <Form2.Checkbox field="enableDelete" label="Allow branch to use delete buttons" />
            <Form2.Checkbox
              field="enableOnHoldOrders"
              label="Allow creation of orders for customers that are on hold"
            />
            <Form2.Checkbox
              field="useAverageBuyingPrice"
              label="Use the average buying price instead of current cost price"
            />
            <Form2.Checkbox
              field="useQuoteSpecialItems"
              label="Allow quote items to be flagged as special order items"
            />
            <Form2.Text
              field="salesRepAnalysisCode"
              postFieldText={
                <React.Fragment>
                  <P size="small">
                    The name of the analysis code used to associate Sales Reps to customers
                  </P>
                  <P size="small">If left blank, will default to "Sales Rep"</P>
                </React.Fragment>
              }
            />
            <Form2.Checkbox
              field="showSagePurchaseOrdersOnGoodsReceiptsPage"
              label="Show Sage Purchase Orders on the Goods Receipts page"
            />
          </SettingsPanel>
        </Grid>
      ),
    },
    {
      header: "Analysis Codes",
      key: "analysis-codes",
      icon: "",
      Content: () => <AnalysisCodesForm />,
    },
  ]
  const [tab, setTab] = useState(formTabs[0].key)
  const filteredTabs = formTabs.filter(v => !v.hide)

  const MemoComponent = React.useMemo(
    filteredTabs.find(t => t.key === tab)?.Content ?? (() => null),
    [tab],
  )

  return (
    <Form2.Form
      xRef={formRef}
      initialData={{
        active: true,
        ...defaultSettings,
        ...branch?.settings,
        useExplicitAllocations: !!branch?.settings?.useExplicitAllocations,
        pdfDiscrepancyTime: branch?.settings?.pdfDiscrepancyTime?.toString() ?? "",
        pdfReturnsTime: branch?.settings?.pdfReturnsTime?.toString() ?? "",
        adminSettings: {
          appearsOnStockLookup: branch?.appearsOnStockLookup,
          is_fortus_ew_branch: branch?.is_fortus_ew_branch,
          branch_charges_pennies: branch?.branch_charges_pennies,
          branch_overhead_charges_pennies: branch?.branch_overhead_charges_pennies,
        },
        currency_name: branch?.currency_name,
        euro_pound_exchange_rate: branch?.euro_pound_exchange_rate,
        consignmentWarehouse: branch?.consignmentWarehouse?.id,
      }}
      onSubmit={handleSave}
      onChange={data => {
        setDpdEnabled(!!(data.dpdService !== "none"))
      }}
    >
      <NewForm2Submit value="Save" />
      <ControlledPageTabs
        onTabChange={setTab}
        selectedTab={tab}
        tabs={filteredTabs.map(tab => ({
          children: tab.header,
          tabKey: tab.key,
          icon: tab.icon,
        }))}
      />
      {MemoComponent}

      <NewForm2Submit value="Save" />
    </Form2.Form>
  )
}

export { getAnalysisCodeAttr, popFields, sopFields, cuFields, analysisCodeFields }

function AnalysisCodesForm() {
  function RenderFields({ fields }: { fields: any }) {
    const textFields = ["analysisCodeCuBranchCode"]
    return (
      <Grid gap={8}>
        {fields.map((fieldAttr: string) =>
          textFields.includes(fieldAttr) ? (
            <Form2.Text key={fieldAttr} label={titleize(fieldAttr)} field={fieldAttr} />
          ) : (
            <Form2.Number key={fieldAttr} label={titleize(fieldAttr)} field={fieldAttr} />
          ),
        )}
      </Grid>
    )
  }

  return (
    <Grid gap={16}>
      <H2>Purchase Orders</H2>
      <RenderFields fields={popFields} />
      <H2>Sales Orders</H2>
      <RenderFields fields={sopFields} />
      <H2>Customers</H2>
      <RenderFields fields={cuFields} />
    </Grid>
  )
}

function MultiProductSelect({ field, branchId }: { field: string; branchId: string }) {
  const skeem = window.skeem

  async function loadProducts(query: string) {
    return skeem.fetch2<IRecord<"products", "stock_code" | "body">>({
      model: {
        name: "products",
        attributes: ["stock_code", "body"],
        paginate: { size: 15 },
        sort: { by: "stock_code", direction: "desc" },
        filter: {
          and: [
            { matches: { branch: branchId } },
            {
              or: [
                { matches: { stock_code: { like: query } } },
                { matches: { body: { like: query } } },
              ],
            },
          ],
        },
      },
    })
  }

  return (
    <Form2.WithField<{ id: string; stock_code: string; body: string }[]>
      field="alwaysStockCheckProducts"
      render={formField => (
        <Form2.AsyncSelect
          field={field}
          onChange={formField.setValue}
          value={formField.getValue()}
          loadOptions={loadProducts}
          getOptionValue={opt => opt.id}
          formatOptionLabel={opt => (
            <div>
              <P blockSpacing={0} size="small">
                {opt.stock_code}
              </P>
              <P blockSpacing={0}>{opt.body}</P>
            </div>
          )}
          isMulti
        />
      )}
    />
  )
}

type IAddressData = IRecord<
  "addresses",
  | "name"
  | "line_1"
  | "line_2"
  | "line_3"
  | "line_4"
  | "city"
  | "county"
  | "country"
  | "postcode"
  | "phone"
  | "email"
>
export type IORBranchIRecord = IRecord<
  "orderRequestBranches",
  | "targetBranch"
  | "sourceBranch"
  | "supplier"
  | "shippingAddress"
  | "billingAddress"
  | "customer"
  | "autoExport"
  | "default"
  | "createdAt"
> & {
  targetBranch: IRecord<"branches", "name"> & { settings: IBranchSettings }
  sourceBranch: IRecord<"branches", "name"> & { settings: IBranchSettings }
  supplier: IRecord<"suppliers", "name">
  customer: IRecord<"customers", "name">
  contact: IRecord<"contacts", "name" | "email">
  shippingAddress: IAddressData
  billingAddress: IAddressData
}

interface IEditableOrderRequestBranch {
  id?: string
  target?: string
  targetBranch: { id: string }
  customerId: string
  supplierId: string
  shippingAddress: { id: string }
  billingAddress: { id: string }
  autoExport: boolean
  default: boolean

  contactId: string
}

const addressAttrs = [
  "name",
  "line_1",
  "line_2",
  "line_3",
  "line_4",
  "city",
  "county",
  "country",
  "postcode",
  "phone",
  "email",
]
export const orderRequestBranchAttributes = [
  { name: "targetBranch", attributes: ["name", "settings"] },
  { name: "sourceBranch", attributes: ["name", "settings"] },
  { name: "customer", attributes: ["name"] },
  { name: "supplier", attributes: ["name"] },
  { name: "shippingAddress", attributes: addressAttrs },
  { name: "billingAddress", attributes: addressAttrs },
  "autoExport",
  "default",
  "createdAt",
  { name: "contact", attributes: ["name", "email"] },
]

export function ManageOrderRequestBranches({ branchId }: { branchId?: string }) {
  const alerts = useSiteAlerts()
  const skeem = window.skeem
  const [orderRequestBranches, setOrderRequestBranches] = useState<IORBranchIRecord[]>([])

  async function loadState() {
    const result = await skeem.fetch2<{ orderRequestBranches: IORBranchIRecord[] }>({
      first: {
        name: "branches",
        matches: { id: branchId },
        attributes: [
          {
            name: "orderRequestBranches",
            attributes: orderRequestBranchAttributes,
            sort: { by: "createdAt", direction: "asc" },
          },
        ],
      },
    })
    setOrderRequestBranches(result?.orderRequestBranches ?? [])
  }
  useAsyncEffect(loadState, [branchId])

  async function handleEditOrCreate(data?: IORBranchIRecord) {
    const handled = await handleEdit(data ?? {})
    if (handled) {
      loadState()
    }
  }

  const [confirmDelete, handleConfirmDelete] = useConfirmCancelConfirmation({
    heading: "Are you sure you want to delete this order request branch?",
  })

  async function handleDeleteOrderRequestBranch({ id }: { id: string }) {
    if (await handleConfirmDelete()) {
      await skeem.mutate({ orderRequestBranches: { destroy: id } })
      loadState()
      alerts.genericSuccess("Order request branch deleted")
    }
  }

  const [node, handleEdit] = useConfirmation<Partial<IORBranchIRecord>, boolean>(
    ({ data, onResolve }) => {
      async function handleSubmit(newData: Partial<IEditableOrderRequestBranch>, state: FormState) {
        console.log("Submitted", newData)
        const targetBranchField = state.getField("targetBranch")
        if (!data.id) {
          const result = await skeem.fetch2<{
            orderRequestBranches: {
              targetBranch: { id: string }
            }[]
          }>({
            first: {
              name: "branches",
              matches: { id: branchId },
              attributes: [
                {
                  name: "orderRequestBranches",
                  attributes: ["targetBranch"],
                },
              ],
            },
          })
          if (result.orderRequestBranches.find(x => x.targetBranch.id == newData.targetBranch?.id)) {
            targetBranchField.setError("Branch already has an order request branch set up.")
            return
          }

          targetBranchField.setError(undefined)

          await skeem.mutate({
            orderRequestBranches: {
              create: {
                sourceBranch: { set: branchId },
                targetBranch: { set: newData.targetBranch?.id ?? null },
                supplier: { set: newData.supplierId },
                customer: { set: newData.customerId },
                shippingAddress: { set: newData.shippingAddress?.id },
                billingAddress: { set: newData.billingAddress?.id },
                autoExport: newData.autoExport,
                default: newData.default,

                contact: { set: newData.contactId ?? null },
              },
            },
          })
          alerts.genericSuccess("Order request branch created")
        } else {
          await skeem.mutate({
            orderRequestBranches: {
              update: {
                id: data.id,
                targetBranch: { set: newData.targetBranch?.id ?? null },
                supplier: { set: newData.supplierId },
                customer: { set: newData.customerId },
                shippingAddress: { set: newData.shippingAddress?.id },
                billingAddress: { set: newData.billingAddress?.id },
                autoExport: newData.autoExport,
                default: newData.default,

                contact: { set: newData.contactId ?? null },
              },
            },
          })
          alerts.genericSuccess("Order request branch updated")
        }
        onResolve(true)
      }

      return (
        <Form2.Form
          onSubmit={handleSubmit}
          initialData={{
            targetBranch: data?.targetBranch,
            supplierId: data?.supplier?.id,
            customerId: data?.customer?.id,
            shippingAddress: data.shippingAddress,
            billingAddress: data.billingAddress,
            autoExport: data.autoExport,
            default: data.default,

            contactId: data.contact?.id,
          }}
        >
          <Form2.WithForm
            subscribe={{ dataChange: true }}
            render={form => {
              const targetBranchField = form.getField("targetBranch")
              return (
                <div
                  css={css`
                    .overflow-v {
                      overflow: visible !important;
                    }

                    [aria-modal="true"] > :first-child {
                      overflow: visible !important;
                    }
                  `}
                >
                  <ConfirmCancelDialogue
                    heading={`${data.id ? "Edit" : "New"} Order Request Branch`}
                    onConfirm={event => {
                      event.preventDefault()
                      event.stopPropagation()
                      form.submit()
                    }}
                    onCancel={() => onResolve(false)}
                    isOpen={true}
                    width={800}
                    className="overflow-v"
                    body={
                      <Grid
                        gap={8}
                        css={css`
                          padding: 2px;
                        `}
                      >
                        <React.Fragment>
                          <React.Fragment>
                            <BranchSelect
                              onChange={branch => targetBranchField.setValue(branch)}
                              value={targetBranchField.getValue() ?? undefined}
                              error={targetBranchField.getError()}
                            />
                            <Form2.WithField<string | null>
                              field="supplierId"
                              render={field => (
                                <SupplierSelect
                                  label="Supplier to use to represent the branch the request goes to"
                                  value={field.getValue() ?? ""}
                                  onChange={field.setValue}
                                  branchId={branchId}
                                />
                              )}
                            />

                            <Form2.WithField<string | null>
                              field="customerId"
                              render={field => (
                                <React.Fragment>
                                  <CustomerSelectList2
                                    label="Customer to represent the branch to send the request to"
                                    value={field.getValue() ?? ""}
                                    onChange={field.setValue}
                                    branchId={targetBranchField.getValue()?.id}
                                  />

                                  <Form2.WithField<string>
                                    field="contactId"
                                    render={contactField => (
                                      <ContactSelectList
                                        label="RMA Contact"
                                        value={contactField.getValue()}
                                        onChange={id => contactField.setValue(id)}
                                        customerId={field.getValue() ?? undefined}
                                        disabled={!field.getValue()}
                                        branchId={targetBranchField.getValue()?.id}
                                        showNoRecordsMessage
                                        isClearable
                                      />
                                    )}
                                    subscribe
                                  />
                                </React.Fragment>
                              )}
                            />

                            <Form2.WithField<string>
                              field="customerId"
                              render={field => (
                                <OrderRequestCustomerAddressSelect
                                  label="Order request default billing address"
                                  customerId={field.getValue()}
                                  fieldName="billingAddress"
                                  disabled={!field.getValue()}
                                  postFieldText={
                                    !field.getValue() ? (
                                      <P size="small">
                                        Select a customer to change default billing address
                                      </P>
                                    ) : undefined
                                  }
                                />
                              )}
                              subscribe
                            />
                            <Form2.WithField<string>
                              field="customerId"
                              render={field => (
                                <OrderRequestCustomerAddressSelect
                                  label="Order request default shipping address"
                                  customerId={field.getValue()}
                                  fieldName="shippingAddress"
                                  disabled={!field.getValue()}
                                  postFieldText={
                                    !field.getValue() ? (
                                      <P size="small">
                                        Select a customer to change default shipping address
                                      </P>
                                    ) : undefined
                                  }
                                />
                              )}
                              subscribe
                            />
                          </React.Fragment>
                        </React.Fragment>

                        <Form2.Checkbox
                          field="autoExport"
                          label="Automatically export Order requests made to this branch from the target."
                        />
                        <Form2.Checkbox field="default" label="Set this one as the default." />
                      </Grid>
                    }
                  />
                </div>
              )
            }}
          />
        </Form2.Form>
      )
    },
  )

  return (
    <React.Fragment>
      <SettingsPanel header="Internal Branches Settings">
        <H3>Order Requests</H3>
        <Form2.Text field="orderRequestIdentifierPrefix" />
        <Form2.Number field={"consolidateOrderRequestsMinute"} />
        <Form2.Checkbox field={"allowOrderRequestConsolidation"} />
        <Form2.Checkbox
          field="autoExportOrderRequests"
          postFieldText={
            <P size="small" blockSpacing={0}>
              When enabled by both creating and receiving branches, order requests will
              automatically be validated and attempt to create a straight through order export.
            </P>
          }
        />
      </SettingsPanel>
      {document.getElementById("app-scene-portal-ref") &&
        createPortal(
          <React.Fragment>
            {node}
            {confirmDelete}
          </React.Fragment>,
          document.getElementById("app-scene-portal-ref")!,
        )}
      <SettingsPanel
        header="Internal Branches"
        subtitle={<P>Changes made are saved when submitting the modal.</P>}
        footer={
          <div>
            <Button leftIcon={icons.plus} onClick={() => handleEditOrCreate()}>
              Add branch
            </Button>
          </div>
        }
      >
        <HorizontalOverflow minWidth={1200}>
          <Table
            data={orderRequestBranches.sort((a, b) => {
              const aDate = new Date(a.createdAt).getTime()
              const bDate = new Date(b.createdAt).getTime()
              return aDate - bDate
            })}
          >
            <Table.Column heading="Branch">
              {row => {
                return <div>{row.targetBranch.name}</div>
              }}
            </Table.Column>
            <Table.BooleanColumn
              field="autoExport"
              heading="Auto export"
              renderTrue={() => <Icon icon={icons.check} />}
            />
            <Table.BooleanColumn
              field="default"
              heading="Default"
              renderTrue={() => <Icon icon={icons.check} />}
            />
            <Table.DataColumn field="customer" heading="Customer" render={b => b?.name} />
            <Table.DataColumn field="contact" heading="Contact" render={b => b?.name} />
            <Table.DataColumn field="supplier" heading="Supplier" render={b => b?.name} />
            <Table.DataColumn
              field="shippingAddress"
              heading="Shipping Address"
              render={opt => (opt ? `${opt.name}, ${opt.line_1}, ${opt.postcode}` : "")}
            />
            <Table.DataColumn
              field="billingAddress"
              heading="Billing Address"
              render={opt => (opt ? `${opt.name}, ${opt.line_1}, ${opt.postcode}` : "")}
            />
            <MenuColumn
              items={row => [
                { children: "Edit", onClick: () => handleEditOrCreate(row) },
                { children: "Delete", onClick: () => handleDeleteOrderRequestBranch(row) },
              ]}
            />
          </Table>
        </HorizontalOverflow>
      </SettingsPanel>
    </React.Fragment>
  )
}

export function SettingsPanel({
  header,
  children,
  subtitle,
  gridGap,
  footer,
}: {
  header?: string
  children: React.ReactNode
  subtitle?: string | React.ReactNode
  gridGap?: number
  footer?: React.ReactNode
}) {
  return (
    <FloatingBox
      header={
        header || subtitle ? (
          <React.Fragment>
            {header && <H4>{header}</H4>}
            {subtitle ? typeof subtitle === "string" ? <P>{subtitle}</P> : subtitle : null}
          </React.Fragment>
        ) : undefined
      }
      footer={footer}
    >
      <Grid gap={gridGap ?? 8}>{children}</Grid>
    </FloatingBox>
  )
}

function EosIDPrefixValidation() {
  Form2.useFormSubmitLock(state => {
    const field = state.getField("exportEosIdentifierPrefix")
    const value = field.getValue() as string
    if (value.length > 0) {
      const matches = /(\.)|(_)|(\+)|(\s)|(,)/g.exec(value)
      if (matches) {
        field.setError("Prefix cannot contain the follow characters . , _ + or spaces")
        return false
      }
    }
    field.setError(undefined)
  })

  return null
}

function WarehouseWatcher() {
  // Removes values from stock warehouse if they are not in warehouses
  const warehousesField = Form2.useField<IBranchSettings["warehouses"]>("warehouses")
  const stockWarehousesField = Form2.useField<IBranchSettings["stockWarehouses"]>("stockWarehouses")
  warehousesField.events.on(fieldEvents.change, () => {
    const warehouses = warehousesField.getValue()
    const stockWarehouses = stockWarehousesField.getValue()
    if (warehouses.length > 0) {
      if (stockWarehouses.length > 0) {
        const filtered = stockWarehouses.filter(wh => !!warehouses.find(v => v.id === wh.id))
        stockWarehousesField.setValue(filtered)
        stockWarehousesField.triggerChange()
      }
    }
  })
  return null
}

export function DefaultStockLocationField({
  branch,
  field,
}: {
  branch?: IRecord<"branches">
  field?: string
}) {
  const formField = Form2.useField<string>(field ?? "defaultStockLocation", { subscribe: true })
  const skeem = window.skeem

  function loadWarehouses(query: string) {
    return skeem.fetch2<IRecord<"warehouses", "name">[]>({
      model: {
        name: "warehouses",
        attributes: ["name"],
        filter: {
          and: [
            { matches: { branch: branch?.id } },
            query && { matches: { name: { like: query } } },
          ].filter(Boolean),
        },
      },
    })
  }

  return (
    <Fields.AsyncSelect
      label="Default Stock Location"
      loadOptions={loadWarehouses}
      getOptionLabel={opt => opt.name}
      getOptionValue={opt => opt.name}
      onChange={value => formField.setValue(value?.name ?? "")}
      value={formField.getValue() ? { name: formField.getValue(), id: "selected" } : undefined}
      noOptionsMessage={({ inputValue }) =>
        inputValue.length === 0 ? "Type to search..." : "No results"
      }
    />
  )
}

type ICustomer = {
  id: string
  addresses: IRecord<
    "addresses",
    | "name"
    | "line_1"
    | "line_2"
    | "line_3"
    | "line_4"
    | "city"
    | "county"
    | "country"
    | "postcode"
    | "phone"
    | "email"
  >[]
}

type IAddress = IRecord<
  "addresses",
  | "name"
  | "line_1"
  | "line_2"
  | "line_3"
  | "line_4"
  | "city"
  | "county"
  | "country"
  | "postcode"
  | "phone"
  | "email"
>

function OrderRequestCustomerAddressSelect({
  label,
  customerId,
  fieldName,
  postFieldText,
  disabled,
}: {
  label: string
  customerId: string
  fieldName: string
  postFieldText?: React.ReactNode
  disabled?: boolean
}) {
  const field = useField<IAddress>(fieldName, { subscribe: true })
  const skeem = window.skeem
  const [customer, setCustomer] = useState<ICustomer | null>(null)

  useAsyncEffect(async () => {
    const customer = await skeem.fetch2<ICustomer>({
      first: {
        name: "customers",
        attributes: [
          {
            name: "addresses",
            attributes: [
              "name",
              "line_1",
              "line_2",
              "line_3",
              "line_4",
              "city",
              "county",
              "country",
              "postcode",
              "phone",
              "email",
            ],
            filter: {
              or: [
                { matches: { postcode: { not: "" }, country: "United Kingdom" } },
                { matches: { country: { not: "United Kingdom" } } },
              ],
            },
          },
        ],
        filter: {
          and: [{ matches: { id: customerId } }],
        },
      },
    })
    setCustomer(customer)
  }, [customerId])

  return (
    <div>
      <Fields.Select2
        label={label}
        options={customer?.addresses}
        onChange={opt => field.setValue(opt)}
        value={field.getValue()}
        getOptionLabel={(opt: any) => `${opt.name}, ${opt.line_1}, ${opt.postcode}`}
        getOptionValue={opt => opt.id}
        postFieldText={postFieldText}
        disabled={disabled}
      />
      {field.getValue() && typeof field.getValue() === "object" && (
        <div
          css={css`
            background-color: #f5f5f5;
            border-radius: 4px;
            padding: 8px;
          `}
        >
          {Object.values(field.getValue())
            .filter(Boolean)
            .map((line, i) => (
              <div key={i}>{line}</div>
            ))}
        </div>
      )}
    </div>
  )
}

function BranchAddressSelect({
  label,
  fieldName,
  isClearable,
}: {
  label: string
  fieldName: string
  isClearable?: boolean
}) {
  const field = useField<IAddress>(fieldName, { subscribe: true })
  const skeem = window.skeem
  const { currentUser } = useGlobalState()

  async function loadAddresses(query: string) {
    const addresses = await skeem.fetch2<IAddress[]>({
      model: {
        name: "addresses",
        attributes: [
          "name",
          "line_1",
          "line_2",
          "line_3",
          "line_4",
          "city",
          "county",
          "country",
          "postcode",
          "phone",
          "email",
        ],
        filter: {
          and: [
            { matches: { postcode: { not: "" }, branch: currentUser.branch.id } },
            {
              or: [
                "name",
                "line_1",
                "line_2",
                "line_3",
                "line_4",
                "city",
                "county",
                "country",
                "postcode",
                "phone",
                "email",
              ].map(x => ({ matches: { [x]: { like: query } } })),
            },
          ],
        },
        paginate: { size: 20 },
      },
    })
    return addresses
  }

  return (
    <div>
      <Fields.AsyncSelect
        label={label}
        loadOptions={loadAddresses}
        onChange={opt => field.setValue(opt)}
        value={field.getValue()}
        getOptionLabel={(opt: any) => `${opt.name}, ${opt.line_1}, ${opt.postcode}`}
        getOptionValue={opt => opt.id}
        isClearable={isClearable}
      />
      {field.getValue() && typeof field.getValue() === "object" && (
        <div
          css={css`
            background-color: #f5f5f5;
            border-radius: 4px;
            padding: 8px;
          `}
        >
          {Object.values(field.getValue())
            .filter(Boolean)
            .map((line, i) => (
              <div key={i}>{line}</div>
            ))}
        </div>
      )}
    </div>
  )
}

function EmailsFields({ field }: { field: string }) {
  const emailField = Form2.useField<{ email: string; name: string }[]>(field, {
    subscribe: true,
    defaultValue: [{ email: "", name: "" }],
  })

  // Add new item
  function handleAdd() {
    emailField.setValue([...emailField.getValue(), { email: "", name: "" }])
  }

  // Remove item
  function handleRemove(index: number) {
    emailField.setValue(emailField.getValue().filter((_, i) => i !== index))
  }

  // Change item
  function handleChange(index: number, value: { email: string; name: string }) {
    emailField.setValue(spliceArray(emailField.getValue(), index, 1, value))
  }

  return (
    <List>
      {emailField.getValue().map(({ email, name }, i) => (
        <Grid key={i} columns={[140, { fr: 1 }, "max-content"]} gap={8}>
          <Fields.Text
            placeholder="Name"
            value={name}
            onChange={value => handleChange(i, { email, name: value })}
          />
          <Fields.Email
            placeholder="Email"
            value={email}
            onChange={value => handleChange(i, { email: value, name })}
          />
          <IconButton
            buttonStyle="text"
            icon={icons.cross}
            buttonColor="white"
            onClick={() => handleRemove(i)}
            square
          />
        </Grid>
      ))}
      <div>
        <Button onClick={handleAdd} buttonStyle="outline" buttonColor="#333">
          + Add
        </Button>
      </div>
    </List>
  )
}

function CustomerSettingsForm() {
  return (
    <Grid gap={16}>
      <SettingsPanel
        header="Customer Reference Prefixes"
        subtitle={
          <React.Fragment>
            <P>Enter the prefixes used for customer references.</P>
            <P>When a new reference is added, it will be incremented.</P>
            <P>
              E.G. A prefix of <i>{`"PRE-F"`}</i> will produce result in prefix that follow this
              format: <i>{`"PRE-F01, PRE-F02, PRE-F03"`}</i>
            </P>
            <P>
              A prefix cannot end with a number (E.G. <i>{`"PRE-F1, PRE-F3000"`}</i>) are NOT valid
              prefixes
            </P>
          </React.Fragment>
        }
      >
        <Form2.Checkbox
          field="useCustomerReferencePrefix"
          postFieldText={
            <React.Fragment>
              <Spacer />
              <P>
                If enabled, new customer form will allow selecting one of the prefixes supplied and
                automatically generate incrementing references.
              </P>
            </React.Fragment>
          }
        />
        <TextArrayOptionsField
          field="customerReferencePrefixes"
          postFieldText={opt => (
            <P size="small" blockSpacing={0}>
              Examples: <i>{["01", "23", "456"].map(x => [opt, x].join("")).join(", ")}</i>
            </P>
          )}
          inputValidation={value => {
            if (/\d$/.test(value)) {
              return "Value cannot end with a number"
            }
          }}
        />
      </SettingsPanel>
    </Grid>
  )
}

interface IFileboyFile {
  id: string
  name: string
  originalFilename: string
  created_at: string
}
function FileField({
  file,
  onChange,
}: {
  file?: IFileboyFile
  onChange(file?: IFileboyFile): void
}) {
  const idRef = useRef<string>(file?.id ?? Math.random().toString(36).substring(2, 15))
  return (
    <div>
      {file?.id && (
        <Fields.Text
          label="Display name"
          value={file.name}
          onChange={value => onChange({ ...file, name: value })}
        />
      )}
      <FileUploader
        key={file?.id ?? idRef.current}
        onFileUploaded={file => {
          onChange({
            id: file.id,
            name: file.originalFilename,
            originalFilename: file.originalFilename,
            created_at: new Date().toJSON(),
          })
        }}
        defaultFileId={file?.id ?? ""}
        render={({ DropZone, isUploading, openFileDialogue, error }) => (
          <div>
            {file?.id ? (
              <div>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <div
                    css={css`
                      width: 48px;
                    `}
                  >
                    <FilePreview id={file.id} />
                  </div>
                  <P>{file.originalFilename}</P>
                </div>
                <Button
                  buttonColor="red"
                  leftIcon={icons.delete}
                  onClick={() => onChange(undefined)}
                >
                  Remove document
                </Button>
              </div>
            ) : (
              <DropZone>
                <div
                  css={css`
                    border: 1px dashed ${error ? themeColors.alertRed : colors.lightBorder};
                    border-radius: 6px;
                    padding: 16px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    cursor: pointer;
                    background-color: #f4f4f4;
                  `}
                  onClick={openFileDialogue}
                >
                  {isUploading ? (
                    <Spinner color="black" />
                  ) : (
                    <div>
                      <P size="large" align="center">
                        <Icon icon={icons.upload} /> Upload document
                      </P>
                      <Spacer size={4} />
                      <P align="center" size="small">
                        drag and drop files to upload
                      </P>
                    </div>
                  )}
                </div>
              </DropZone>
            )}
            {error && (
              <P color="error" blockSpacing={0} size="small">
                {error}
              </P>
            )}
          </div>
        )}
      />
    </div>
  )
}
